import { useRootStore } from 'hooks/rootStore'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { captureException } from '@sentry/react'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { useTranslationTyped } from 'i18n/i18n'
import { TriangleAlertFillIcon } from 'components/icons/TriangleAlertFill'

/**
 * Shown while the student still owes answers to the End-of-Session quiz and the
 * window to return is open. It deliberately replaces the completed/results view
 * rather than adding a button to it: those results spell out the correct quiz
 * answers, so a student who has not taken the quiz must not reach them.
 *
 * For the same reason it offers no way into the quiz content: the summary
 * button is shown disabled with an explanatory tooltip, and "View Experience"
 * is withheld entirely — its preview drops the student into an interactive quiz
 * runner for the very quiz they have not taken. The only action out is finishing
 * the quiz.
 */
export const CompletedViewFinishQuizNeeded = observer(
  function CompletedViewFinishQuizNeeded() {
    const cubit = useStudentAssignmentCubit()
    const { t, tt } = useTranslationTyped()
    const store = useRootStore()

    const summary = cubit.roomStateSummary

    return (
      <div
        className={classNames(
          `mt-1 flex flex-1 flex-col items-center justify-center py-5 text-center`
        )}
      >
        <div className="text-body-medium text-grey-text">
          {t('dart.experience')}
        </div>
        <h2 className="mt-3 text-title-large">
          <TriangleAlertFillIcon
            size={20}
            className="mr-1 inline-block align-top text-core-error"
          />
          {tt.student_assignment.finish_quiz_pending()}
        </h2>
        <div className="mt-6">
          <div className="flex flex-row flex-wrap justify-center gap-2">
            {summary?.hasData && (
              <BreakoutTooltip
                placement="top"
                content={tt.student_assignment.finish_quiz_before_summary()}
              >
                <div>
                  <BreakoutButton
                    data-testid="view-summary-button"
                    disabled
                    className="inline-block whitespace-nowrap rounded-2xl bg-core-secondary px-6 py-2 text-body-large text-core-on-tertiary"
                  >
                    {t('student_assignment.view_summary')}
                  </BreakoutButton>
                </div>
              </BreakoutTooltip>
            )}
            <BreakoutButton
              data-testid="assignment-finish-quiz-button"
              kind="accent"
              onClick={() => {
                const roomId = cubit.roomStateId
                if (!roomId) {
                  // canFinishEndOfSessionQuiz implies the room started, so this
                  // is unreachable — report it rather than dead-clicking.
                  captureException(
                    new Error(
                      'Finish Quiz clicked without a roomStateId on the student assignment page'
                    )
                  )
                  return
                }
                store.navigateTo('meeting', { roomId })
              }}
              className="ml-1 inline-block whitespace-nowrap rounded-2xl bg-core-secondary px-6 py-2 text-body-large text-core-on-tertiary"
            >
              {tt.library.actions.finish_quiz()}
            </BreakoutButton>
          </div>
        </div>
      </div>
    )
  }
)
